import { languageName, offeredLanguages } from '@lib/customization/languages';
import { useLocalization } from '@lib/localization';

import {
  Alert, Box, Button, Chip, Divider, Drawer, IconButton, MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import {
  buttonStyles, drawerBodyStyles, drawerCloseStyles, drawerFooterStyles, drawerHeaderStyles, drawerPaperStyles,
  localeCodeChipStyles, primaryChipStyles,
} from '@pages/CustomPoiTypes.styles';
import { type CustomizationLocales } from '@store/api/rootEndpoints';
import { useEffect, useState } from 'react';
import { GlobeIcon, TimesIcon, TrashIcon } from 'react-line-awesome';

interface Props {
  open: boolean;
  locales: CustomizationLocales;
  saving: boolean;
  onClose: () => void;
  onSave: (locales: CustomizationLocales) => void;
}

/**
 * Server-wide locale configuration for user-authored customization content.
 *
 * Edits are held locally and written in one `PUT /config`, so removing three languages
 * and picking a new primary is a single change rather than three half-applied ones.
 */
const LocalesDrawer: React.FC<Props> = ({ open, locales, saving, onClose, onSave }) => {
  const { t, currentLanguage } = useLocalization();

  const [draft, setDraft] = useState<CustomizationLocales>(locales);
  const [toAdd, setToAdd] = useState('');

  // The draft starts from the stored set each time the drawer is opened, and again if that set
  // changes under it. The effect goes by the set's two parts and not by `locales` as a whole,
  // which is another object when the reader's interface language changes as well
  // (`useCustomizationLocales`), and that says nothing about the set. The cache keeps the two
  // parts as they are until the stored set changes, so a re-render of the host, a save on its
  // way, a save that was refused and a change of the reader's language leave an edit alone.
  useEffect(() => {
    if (!open) return;
    setDraft({ primary: locales.primary, enabled: [...locales.enabled] });
    setToAdd('');
  }, [open, locales.primary, locales.enabled]);

  const available = offeredLanguages().filter((code) => !draft.enabled.includes(code));

  const add = () => {
    if (!toAdd) return;
    setDraft((current) => ({ ...current, enabled: [...current.enabled, toAdd] }));
    setToAdd('');
  };

  // The primary has to stay enabled — the API rejects a configuration where it is not —
  // so it can only be removed by promoting another language first.
  const remove = (code: string) => setDraft((current) => ({
    ...current,
    enabled: current.enabled.filter((enabled) => enabled !== code),
  }));

  const isDirty = draft.primary !== locales.primary || draft.enabled.join() !== locales.enabled.join();

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{ paper: { sx: { ...drawerPaperStyles, width: { xs: '100%', sm: 440 } } } }}
    >
      <Stack sx={{ height: '100%' }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={drawerHeaderStyles}>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: 16, lineHeight: 1.4 }}>
              {t('Customization_EditLocales')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              {t('Customization_LocalesSubtitle')}
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose} sx={drawerCloseStyles} disabled={saving}>
            <TimesIcon />
          </IconButton>
        </Stack>

        <Divider />

        <Box sx={{ ...drawerBodyStyles, flex: 1, overflowY: 'auto' }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            {t('Customization_PrimaryHint')}
          </Typography>

          <Stack spacing={1}>
            {draft.enabled.map((code) => {
              const isPrimary = code === draft.primary;

              return (
                <Stack
                  key={code}
                  direction="row"
                  alignItems="center"
                  spacing={1.5}
                  sx={{
                    px: 1.5, py: 1.25, borderRadius: 1,
                    border: '1px solid', borderColor: 'divider',
                  }}
                >
                  <Chip size="small" label={code.toUpperCase()} sx={localeCodeChipStyles} />
                  <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
                    {languageName(code, currentLanguage)}
                  </Typography>

                  {isPrimary
                    ? <Chip size="small" color="primary" label={t('Customization_PrimaryLocale')} sx={primaryChipStyles} />
                    : (
                      <>
                        <Button
                          size="small"
                          sx={buttonStyles}
                          onClick={() => setDraft((current) => ({ ...current, primary: code }))}
                        >
                          {t('Customization_MakePrimary')}
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          startIcon={<TrashIcon />}
                          sx={buttonStyles}
                          onClick={() => remove(code)}
                        >
                          {t('Customization_RemoveLanguage')}
                        </Button>
                      </>
                    )}
                </Stack>
              );
            })}
          </Stack>

          <Stack direction="row" spacing={1.5} sx={{ mt: 3 }}>
            <TextField
              select fullWidth size="small"
              label={t('Customization_AddLanguage')}
              value={toAdd}
              disabled={!available.length}
              onChange={(event) => setToAdd(event.target.value)}
            >
              {available.map((code) => (
                <MenuItem key={code} value={code}>{languageName(code, currentLanguage)}</MenuItem>
              ))}
            </TextField>
            <Button variant="outlined" size="small" disabled={!toAdd} onClick={add} sx={buttonStyles}>
              {t('Common_Add')}
            </Button>
          </Stack>

          <Alert severity="info" icon={<GlobeIcon />} sx={{ mt: 3 }}>
            {t('Customization_RemoveNonDestructive')}
          </Alert>
        </Box>

        <Divider />

        <Stack direction="row" alignItems="center" spacing={1} sx={drawerFooterStyles}>
          <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1, minWidth: 0 }} noWrap>
            {t('Customization_LocalesCount', { count: draft.enabled.length, 1: draft.primary.toUpperCase() })}
          </Typography>
          <Button color="inherit" size="small" onClick={onClose} sx={buttonStyles} disabled={saving}>
            {t('Common_Cancel')}
          </Button>
          <Button
            variant="contained"
            size="small"
            disableElevation
            disabled={!isDirty || saving}
            onClick={() => onSave(draft)}
            sx={buttonStyles}
          >
            {t('Common_Save')}
          </Button>
        </Stack>
      </Stack>
    </Drawer>
  );
};

export default LocalesDrawer;
